import { useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Mail, ShieldAlert } from "lucide-react";
import avatarElaine from "@/assets/testimonials/avatar-elaine-B3rNOmMI.png";
import avatarTarrn from "@/assets/testimonials/avatar-tarrn-DBz18DZ_.png";
import avatarLissa from "@/assets/testimonials/avatar-lissa-B28uOaHv.png";
import avatarAhriana from "@/assets/testimonials/avatar-ahriana-BmR4_NC6.png";
import avatarCarrie from "@/assets/testimonials/avatar-carrie-DtFoRfBZ.png";
import callBookedVideo from "@/assets/call-booked-video.mp4.asset.json";
import thankYouPoster from "@/assets/thank-you-poster.jpg";

const successTestimonials = [
  { quote: "Always pleasant, upbeat, and open to feedback, making the process smooth and enjoyable. Your creativity and attention to detail really shone through, and you were able to translate my ideas into a beautiful, functional website.", name: "Elaine E.", avatar: avatarElaine },
  { quote: "You taught me more than I had previously learned from paying two other business coaches. And the experience of working together was fun!", name: "Tarrn M.", avatar: avatarTarrn },
  { quote: "Absolutely amazing. Sierra really cares about her clients and helps them achieve their goals. You will not regret working with her.", name: "Lissa A.", avatar: avatarLissa },
  { quote: "Now, this website feels like ME — Like people can actually feel who I am and see what I'm up to! YOU TOTALLY ROCK!", name: "Ahriana P.", avatar: avatarAhriana },
  { quote: "I love how you asked great questions and had creative ways of figuring out what we wanted. The end result really shows that part of us. You helped us send the message we were hoping to send.", name: "Carrie S.", avatar: avatarCarrie },
];

export const Route = createFileRoute("/call-booked-success")({
  head: () => ({
    meta: [
      { title: "Your call is booked — Three Day Launch" },
      {
        name: "description",
        content:
          "Thanks for booking your call with Three Day Launch. Check your inbox for confirmation details.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Your call is booked — Three Day Launch" },
      {
        property: "og:description",
        content:
          "Thanks for booking your call with Three Day Launch. Check your inbox for confirmation details.",
      },
    ],
  scripts: [
      {
        children: "gtag('event', 'page_view', {});",
      },
      {
        children:
          'oaiq("measure","registration_completed",{type:"customer_action",amount:0,currency:"USD"});',
      },
      {
        children:
          'if(window.location.href.includes("/call-booked-success")){oaiq("measure","appointment_scheduled",{type:"customer_action"});}',
      },
    ],
  }),
  component: CallBookedSuccessPage,
});

function CallBookedSuccessPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);

  const handleVideoClick = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    setIsMuted(false);
    if (video.paused) {
      video.play();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto max-w-2xl px-6 pt-8 pb-12">
        <div className="text-center">
          <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <CheckCircle2 className="h-9 w-9 text-primary" />
          </div>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            You're booked!
          </div>
          <h1 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight">
            Don't go anywhere yet:
          </h1>
          <p className="mt-4 text-muted-foreground text-lg">
            Watch the video on this page before we meet.
          </p>
          <p className="mt-2 text-foreground font-bold">
            Yes, I will ask if you've watched it!
          </p>
        </div>

        <div className="mt-8 relative rounded-3xl overflow-hidden shadow-card-soft bg-secondary aspect-video">
          <video
            ref={videoRef}
            className="w-full h-full object-cover cursor-pointer"
            autoPlay
            muted
            loop
            playsInline
            controls
            preload="auto"
            poster={thankYouPoster}
            onClick={handleVideoClick}
          >
            <source src={callBookedVideo.url} type="video/mp4" />
          </video>
          {isMuted && (
            <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black/60 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm">
              Tap to unmute
            </div>
          )}
        </div>

        <div className="mt-10 space-y-4">
          <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-6 shadow-card-soft">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Check your inbox</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                You'll get a confirmation email from{" "}
                <a
                  href="mailto:hello@threedaylaunch.com"
                  className="font-medium text-primary underline underline-offset-2"
                >
                  hello@threedaylaunch.com
                </a>{" "}
                with your call details and a calendar invite.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-6 shadow-card-soft">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <ShieldAlert className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Don't see it?</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Peek in your spam or promotions folder just in case, and mark it as
                "not spam" so future emails land in your inbox.
              </p>
            </div>
          </div>
        </div>

      </main>

      <SuccessTestimonials />
    </div>
  );
}

function SuccessTestimonials() {
  const loop = [...successTestimonials, ...successTestimonials];
  return (
    <section className="py-20 bg-background overflow-hidden border-t border-border">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold leading-tight">
            Get ready... because your website is going to be freakin' fantastic!!{" "}
          </h2>
          <p className="mt-3 text-3xl md:text-5xl font-bold text-primary leading-tight">
            {"\u200B"}
          </p>
        </div>
      </div>
      <div className="group relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
        <div className="flex w-max gap-6 animate-marquee group-hover:[animation-play-state:paused]">
          {loop.map((t, i) => (
            <figure
              key={`${t.name}-${i}`}
              className="w-[340px] md:w-[400px] shrink-0 rounded-2xl bg-card border border-border p-8 flex flex-col shadow-sm"
            >
              <div
                className="text-primary text-8xl leading-none font-black mb-2"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
              >
                “
              </div>
              <blockquote className="text-foreground/80 leading-relaxed flex-1">
                {t.quote}
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="h-12 w-12 rounded-full object-cover"
                  loading="lazy"
                />
                <div className="font-semibold">{t.name}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
